import React, { useState, useEffect } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, TextField, Button, List, ListItem, Divider, Radio, RadioGroup, FormControlLabel, IconButton, Box, Typography } from '@mui/material';
import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import { StyledBlueButton, StyledCancelButton } from '../StyledComponent';
import { get, post } from '../../services/api-services';
import { useCommon } from '../../context/CommonContext';

const TemplateDialog = ({ open, setOpen, onClose, onTemplateSelect, onSearch, template, setTemplate }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [templateName, setTemplateName] = useState('');
    const [templates, setTemplates] = useState([]);
    const [filteredTemplates, setFilteredTemplates] = useState([]);
    const [openNewTemplateDialog, setOpenNewTemplateDialog] = useState(false);
    const { setSnackbar } = useCommon();
    useEffect(() => {
        if (open) {
            setSearchTerm('');
            setTemplateName('');
            const fetchTemplates = async () => {
                try {
                    const filterParam = encodeURIComponent(JSON.stringify({ order: 'createdAt DESC' }));
                    const response = await get(`/Templates?filter=${filterParam}`);
                    console.log("response..", response)
                    const data = response?.data;
                    if (response?.statusCode === 200) {
                        setTemplates(data || []);
                        setFilteredTemplates(data || []);
                    } else {
                        setSnackbar({
                            openSnackbar: true,
                            message: 'Something went wrong on fetch templates.',
                            severity: 'error'
                        })
                    }
                } catch (error) {
                    console.error('Error fetching templates', error);
                    setSnackbar({
                        openSnackbar: true,
                        message: `${error}`,
                        severity: 'error'
                    })
                }
            };

            fetchTemplates();
        }
    }, [open, setSnackbar]);

    const handleSubmit = async () => {
        const postData = {
            name: template?.name,
            message: template?.msg,
            createdAt: new Date(),
        };
        try {
            const res = await post('/Templates', postData);
            if (res?.statusCode === 200) {
                setSnackbar({
                    openSnackbar: true,
                    message: 'Template Added Successfully!',
                    severity: 'success'
                })
                onClose();
            }
            else {
                setSnackbar({
                    openSnackbar: true,
                    message: 'Template not added, something went wrong!',
                    severity: 'error'
                })
            }
        } catch (error) {
            console.error('Error adding template', error);
            if (error.response?.status === 401) {
                setSnackbar({
                    openSnackbar: true,
                    message: 'Login expired, please login again.',
                    severity: 'error'
                })
            }
        }
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        const filtered = templates.filter((template) =>
            template.name.toLowerCase().includes(event.target.value.toLowerCase())
        );
        setFilteredTemplates(filtered);

        if (onSearch) {
            onSearch(event.target.value);
        }
    };

    const handleConfirm = () => {
        if (onTemplateSelect && templateName) {
            onTemplateSelect(templateName);
        }
        onClose();
    };

    const handleCreateNewTemplate = () => {
        setOpen(false);
        setOpenNewTemplateDialog(true); // Open the new template dialog
    };

    const handleCloseNewTemplateDialog = () => {
        onClose();
        setOpenNewTemplateDialog(false); // Close the new template dialog
    };

    return (
        <>
            {/* Main Template Dialog */}
            <Dialog open={open} onClose={onClose} fullWidth>
                <DialogTitle>
                    <Box display="flex" alignItems="center">
                        Select/Add Template
                        <Box flex="1" />
                        <IconButton onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Divider />
                </DialogTitle>
                <DialogContent>
                    <Box mb={2}>
                        <TextField
                            fullWidth
                            variant="standard"
                            id="standard-basic"
                            label="Search"
                            value={searchTerm}
                            onChange={handleSearchChange}
                        />
                    </Box>
                    <Divider />
                    <List>
                        {filteredTemplates.length > 0 ? (
                            filteredTemplates.map((templateItem, index) => (
                                <React.Fragment key={index}>
                                    <ListItem
                                        button
                                        onClick={() => setTemplateName(templateItem.message)}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        <Box flex="1">
                                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{templateItem.name}</Typography>
                                            <Typography variant="body2" sx={{ fontSize: '12px', color: '#757575' }}>{templateItem.message}</Typography>
                                        </Box>
                                        <Radio
                                            checked={templateName === templateItem.message}
                                            onChange={() => setTemplateName(templateItem.message)}
                                        />
                                    </ListItem>
                                    <Divider />
                                </React.Fragment>
                            ))
                        ) : (
                            <Box sx={{ py: 2, textAlign: 'center', color: '#888' }}>No templates found</Box>
                        )}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCreateNewTemplate}
                        sx={{ color: (theme) => theme.palette.icon }} endIcon={<AddIcon />}>
                        NEW TEMPLATE
                    </Button>
                    <Box flex="1" />
                    <StyledCancelButton onClick={onClose} variant="outlined" color="default">
                        CANCEL
                    </StyledCancelButton>
                    <StyledBlueButton 
                        variant="contained" 
                        color="#3c50b4"
                        onClick={handleConfirm}
                        disabled={!templateName}
                    >
                        CONFIRM
                    </StyledBlueButton>
                </DialogActions>
            </Dialog>

            {/* New Template Dialog */}
            <Dialog open={openNewTemplateDialog} onClose={handleCloseNewTemplateDialog} fullWidth >
                <DialogTitle>
                    <Box display="flex" alignItems="center">
                        New Template
                        <Box flex="1" />
                        <IconButton onClick={handleCloseNewTemplateDialog}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Divider />
                </DialogTitle>
                <DialogContent className="flex flex-col gap-8">
                    <Box className="flex flex-col gap-3">
                        <Typography className=' font-extrabold'>Define name</Typography>
                        <TextField
                            fullWidth
                            variant="outlined"
                            // label="Define name"
                            value={template?.name}
                            onChange={(e) => setTemplate((prev) => ({ ...prev, name: e.target.value }))}
                            placeholder="Write here"
                            size='small'
                            sx={{
                                backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#040406' : '#f4f4f4',
                                borderRadius: '8px'
                            }}
                        />
                    </Box>
                    <Box className="flex flex-col gap-3">
                        <Typography>Write template message</Typography>
                        <TextField
                            placeholder="Write here"
                            multiline
                            rows={4}
                            fullWidth
                            variant="outlined"
                            value={template?.msg}
                            onChange={(e) => setTemplate((prev) => ({ ...prev, msg: e.target.value }))}
                            autoComplete="off"
                            name="chatMsg"
                            sx={{
                                backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#040406' : '#f4f4f4',
                                borderRadius: '8px'
                            }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <StyledCancelButton onClick={handleCloseNewTemplateDialog} color="default" variant="outlined">
                        CANCEL
                    </StyledCancelButton>
                    <StyledBlueButton onClick={handleSubmit} disabled={!template?.name && !template?.msg} color="primary" variant="contained">
                        ADD
                    </StyledBlueButton>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default TemplateDialog;